// @ts-nocheck
import React, { useState, useEffect, useContext } from 'react';
import { colors } from '@/theme';

import SCTextArea from '@/components/sc-controls/form-controls/sc-textarea';
import SCComboBox from '@/components/sc-controls/form-controls/sc-combobox';
import SCDatePicker from '@/components/sc-controls/form-controls/sc-datepicker';

import * as Enums from '@/utils/enums';
import Helper from '@/utils/helper';
import Time from '@/utils/time';
import EmployeeMultiSelector from '@/components/selectors/employee/employee-multiselector';
import EmployeeService from '@/services/employee/employee-service';
import TaskItemTemplateListOptions from '@/components/modals/task-template/task-item-template-list-options';
import SCSwitch from "@/components/sc-controls/form-controls/sc-switch";
import SCModal from "@/PageComponents/Modal/SCModal";
import {Button, Checkbox, Flex, Loader, Stack, Text, Title} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import Fetch from '@/utils/Fetch';
import TaskTemplatePicker from '@/components/modals/task/task-template-picker';
import { IconArrowLeft } from '@tabler/icons-react';

// Adding a task also covers adding tasks from a template (onTaskItemsFromTemplate): picking a template swaps the
// fields for its task list, where tasks can be unticked before they are added
function ManageTaskItem({ isNew, module, taskItem, onTaskItemSave, onTaskItemsFromTemplate, data, accessStatus }: any) {

  const [template, setTemplate] = useState<{ name: string, items: any[] | null } | null>(null);
  const [skipped, setSkipped] = useState<number[]>([]);

  const pickTemplate = async (picked: any) => {
    setSkipped([]);
    setTemplate({ name: picked.Name, items: null });
    try {
      const response = await Fetch.get({ url: `/TaskTemplate/${picked.ID}` });
      if (!Array.isArray(response?.TaskItemTemplates)) throw new Error(response?.serverMessage || 'Could not load the template');
      setTemplate({ name: picked.Name, items: response.TaskItemTemplates });
    } catch (e: any) {
      setTemplate(null);
      showNotification({ message: e.message || 'Could not load the template', color: 'yellow.7' });
    }
  };

  const templateItems = template?.items ?? [];
  const selectedTemplateItems = templateItems.filter((_: any, i: number) => !skipped.includes(i));

  const addTemplateItems = async () => {
    setSaving(true);
    await onTaskItemsFromTemplate(selectedTemplateItems, template.name);
    setSaving(false);
  };

  const [inputs, setInputs] = useState(isNew ? {
    Description: '',
    DueDate: null,
    Complete: false,
    CompletedDate: null,
    IsActive: true,
    DisplayOrder: 1,
    ItemDataType: null,
    ItemDataOption: null,
    ItemDataResult: null
  } : {
    Description: taskItem.Description,
    DueDate: taskItem.DueDate,
    Complete: taskItem.Complete ? taskItem.Complete : false,
    CompletedDate: taskItem.CompletedDate,
    IsActive: taskItem.IsActive,
    DisplayOrder: taskItem.DisplayOrder,
    ItemDataType: taskItem.ItemDataType,
    ItemDataOption: taskItem.ItemDataOption,
    ItemDataResult: taskItem.ItemDataResult
  });

  const [dataTypeChecked, setDataTypeChecked] = useState(isNew ? false : !Helper.isNullOrWhitespace(taskItem.ItemDataType));

  const [isComplete, setIsComplete] = useState(isNew ? false : taskItem.Complete);

  const handleInputChange = (e: any) => {
    setInputs({
      ...inputs,
      [e.name]: e.value
    });
  };

  const dateChanged = (e: any, fieldName: any) => {
    setInputs({
      ...inputs,
      [fieldName]: Time.toISOString(Time.updateDate(inputs[fieldName], e.value))
    });
  };

  const [selectedEmployees, setSelectedEmployees] = useState<any[]>([]);

  const selectEmployee = (employee: any) => {

    let employeeFound = selectedEmployees.find((x: any) => x.ID == employee.ID);
    if (employeeFound) {
      let temp = selectedEmployees.filter((x: any) => x.ID != employeeFound.ID);
      setSelectedEmployees([...temp]);
    } else {
      setSelectedEmployees([...selectedEmployees, employee]);
    }
  };

  async function getEmployees() {
    let employeeIDs = taskItem.TaskItemEmployees ? taskItem.TaskItemEmployees.map((x: any) => { return x.EmployeeID }) : null;
    if (employeeIDs) {
      const employees = await EmployeeService.getEmployees(data.StoreID);
      setSelectedEmployees(employees.Results.filter((x: any) => employeeIDs.some((y: any) => y == x.ID)));
    }
  }

  useEffect(() => {
    if (!isNew) {
      getEmployees();
    }
  }, []);

  const [inputErrors, setInputErrors] = useState<any>({});
  const [saving, setSaving] = useState(false);

  const validate = () => {
    let validateInputs = [
      { key: "Description", value: inputs.Description, type: Enums.ControlType.Text, required: true }
    ];

    if (dataTypeChecked) {
      validateInputs.push({ key: 'ItemDataType', value: selectedDataType, required: true, type: Enums.ControlType.Select });
    }

    if (showDataOption()) {
      validateInputs.push({ key: 'ItemDataOption', value: inputs.ItemDataOption, required: true, type: Enums.ControlType.Text });
    }

    const { isValid, errors } = Helper.validateInputs(validateInputs);

    setInputErrors(errors);
    return isValid;
  };

  const saveItem = async () => {

    setSaving(true);

    let isValid = validate();
    if (isValid) {

      let taskItemEmployees = selectedEmployees.length > 0 ?
        selectedEmployees.map((employee: any, i: any) => { return { EmployeeID: employee.ID, EmployeeFullName: employee.FullName, TaskItemID: taskItem ? taskItem.ID : null } }) : null;

      let item = {
        ...inputs,
        ID: taskItem ? taskItem.ID : null,
        Module: module,
        ItemID: data.ID,
        TaskItemEmployees: taskItemEmployees,
        //ItemDataType: selectedDataType ? selectedDataType.ID : null,
      };

      await onTaskItemSave(item);
      setSaving(false);
    } else {
      setSaving(false);
      showNotification({
        message: 'There are errors on the page',
        color: 'yellow.7'
      });
    }
  };

  const setDataOption = (option: any) => {
    setInputs({
      ...inputs,
      ItemDataOption: option
    });
  };

  const showDataOption = () => {
    return inputs.ItemDataType === "Select" || inputs.ItemDataType === "MultiSelect";
  }

  const getDataTypeOptions = () => {
    const keys = Object.keys(Enums.TaskTemplateDataTypes);
    return keys.map((key: any) => {
      return {
        ID: key,
        Description: Enums.TaskTemplateDataTypes[key]
      };
    });
  }

  const [selectedDataType, setSelectedDataType] = useState(isNew ? null : taskItem.ItemDataType);

  const getSelectedDataType = () => {
    return inputs.ItemDataType ? {ID: inputs.ItemDataType, Description: Enums.TaskTemplateDataTypes[inputs.ItemDataType]} : null;
  };

  const dataTypeChanged = (option: any) => {
    setInputs({
      ...inputs,
      ItemDataType: option ? option.ID : null
    });
    setInputErrors({
      ...inputErrors,
      ItemDataType: null
    });

    setSelectedDataType(option ? option.ID : null);
  };

  const handleDataTypeCheckedChange = (checked: any) => {
    setDataTypeChecked(checked);
    if (!checked) {
      dataTypeChanged(null);
    }
  }
  return (
    <>
      <SCModal open={true} size={560} onClose={() => onTaskItemSave(null)} modalProps={{closeOnClickOutside: false}}>

        <div className="title" style={{marginTop: 'var(--mantine-spacing-sm)'}}>
          {template ?
              <h1>Adding tasks from {template.name}</h1> :
              isNew ?
              <h1>Adding a Task</h1> :
              <h1>Editing a Task</h1>
          }
        </div>

        {template ? (
          template.items === null ? <Flex justify={'center'} py={'xl'}><Loader size={'sm'}/></Flex> :
          templateItems.length === 0 ? <Text size={'sm'} c={'dimmed'} mt={'md'}>This template has no tasks.</Text> :
          <>
            <Text size={'sm'} c={'dimmed'} mb={'xs'}>
              {selectedTemplateItems.length} of {templateItems.length} tasks will be added. Untick any you don't need.
            </Text>
            <Stack gap={0} mah={320} style={{overflowY: 'auto', border: '1px solid var(--mantine-color-gray-3)', borderRadius: 4}}>
              {templateItems.map((item: any, i: number) =>
                <Flex key={item.ID ?? i} align={'center'} gap={'sm'} px={'sm'} py={8}
                      style={{borderTop: i ? '1px solid var(--mantine-color-gray-2)' : undefined}}>
                  <Checkbox
                      checked={!skipped.includes(i)}
                      onChange={(e: any) => {
                        const ticked = e.currentTarget.checked;
                        setSkipped(s => ticked ? s.filter(x => x !== i) : [...s, i]);
                      }}
                      aria-label={item.Description}
                  />
                  <Text size={'sm'} style={{flex: 1}}>{item.Description}</Text>
                  {item.DataType && <Text size={'xs'} c={'dimmed'}>{Enums.TaskTemplateDataTypes[item.DataType]}</Text>}
                </Flex>
              )}
            </Stack>
          </>
        ) : <>
        <div className="row">
          <div className="column">
            <SCTextArea
                label="Description"
                required={true}
                onChange={(e: any) => handleInputChange({name: "Description", value: e.value})}
                value={inputs.Description}
                error={inputErrors.Description}
                readOnly={isComplete}
            />
          </div>
        </div>

        {isComplete ? "" : <SCSwitch checked={dataTypeChecked}
                                     onToggle={(checked: any) => handleDataTypeCheckedChange(checked)}
                                     label='Add input to task'/>
}

        {dataTypeChecked ? <>
          <div className="row">
            <div className="column">
              <SCComboBox
                  label={"Type"}
                  name={"ItemDataType"}
                  error={inputErrors.ItemDataType}
                  options={getDataTypeOptions()}
                  textField="Description"
                  dataItemKey="ID"
                  onChange={((option: any) => dataTypeChanged(option))}
                  value={getSelectedDataType()}
                  disabled={isComplete}
              />
            </div>
            <div className="column">
              {showDataOption() ? <>
                <h4>List Options</h4>
                <TaskItemTemplateListOptions dataOption={inputs.ItemDataOption} setDataOption={setDataOption}
                                             error={inputErrors.ItemDataOption} disabled={isComplete}/>
              </> : ""}
            </div>
          </div>

        </> : ""}

        <div className="row">
          <div className="column">
            <SCDatePicker
                changeHandler={(date: any) => dateChanged(date, "DueDate")}
                label='Due Date'
                error={inputErrors.DueDate}
                value={inputs.DueDate}
                disabled={isComplete}
                name="DueDate"
            />
          </div>
        </div>
        <div className="row">
          {/* <AssignTechnicians selectedEmployees={selectedEmployees} selectEmployee={selectEmployee} dropdownDirection="up" storeID={data.StoreID} disabled={isComplete} /> */}
          <div className="column">
            <EmployeeMultiSelector selectedEmployees={selectedEmployees} setSelectedEmployees={setSelectedEmployees}
                                   storeID={data.StoreID} disabled={isComplete}/>
          </div>
        </div>
        </>}

        <Flex mt={'md'} justify={'end'} align={'center'} gap={'sm'}>
          {template ?
              <Button variant={'subtle'} size={'compact-sm'} mr={'auto'} leftSection={<IconArrowLeft size={16}/>}
                      onClick={() => setTemplate(null)} disabled={saving}>Back to single task</Button> :
              isNew && onTaskItemsFromTemplate &&
              <div style={{marginRight: 'auto'}}>
                <TaskTemplatePicker module={module} disabled={saving} onPick={pickTemplate}/>
              </div>
          }
          {/*<LegacyButton text="Cancel" extraClasses="hollow auto" onClick={() => onTaskItemSave(null)}/>*/}
          <Button variant={'subtle'} color={'gray.7'} onClick={() => onTaskItemSave(null)} disabled={saving}>Cancel</Button>
          {template ?
              <Button onClick={addTemplateItems} disabled={saving || selectedTemplateItems.length === 0} loading={saving}>
                {selectedTemplateItems.length > 0 ? `Add ${selectedTemplateItems.length} task${selectedTemplateItems.length === 1 ? '' : 's'} to ${module == Enums.Module.JobCard ? 'Job' : 'Query'}` : 'Add to ' + (module == Enums.Module.JobCard ? 'Job' : 'Query')}
              </Button> :
              <Button
                  extraClasses="auto left-margin"
                  onClick={saveItem}
                  disabled={saving}
                  loading={saving}
              >
                {isNew ? `Add to ${module == Enums.Module.JobCard ? 'Job' : 'Query'}` : 'Save'}
              </Button>
          }
        </Flex>

      </SCModal>


      <style jsx>{`
        .modal-container {

        }

        .row {
          display: flex;
          justify-content: space-between;
        }

        .column {
          display: flex;
          flex-direction: column;
          width: 100%;
        }

        .column + .column {
          margin-left: 1.25rem;
        }

        .align-end {
          justify-content: flex-end;
          align-items: flex-end;
        }

        .title {
          color: ${colors.bluePrimary};
          font-size: 1.125rem;
          font-weight: bold;
        }

        .inventory-item-container {
          display: flex;
          flex-direction: row;
          width: 100%;
        }

        .description-container {

        }

        .total-row {
          font-weight: bold;
          margin-top: 1rem;
        }

        .end {
          align-items: flex-end;
        }

        .cancel {

        }

        .update {

        }

        .left-padding {
          padding-left: 0.5em;
        }

        .right-padding {
          padding-right: 0.5em;
        }
      `}</style>
    </>
  );
}

export default ManageTaskItem;
